import React from 'react';
import styled from 'styled-components';
import { MdDirectionsCar, MdAccessTime, MdDescription, MdSend, MdAssessment, MdAttachMoney, MdIntegrationInstructions } from 'react-icons/md';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FeaturesFooter from '../components/FeaturesFooter';

const features = [
  {
    icon: <MdDirectionsCar />, title: 'Mileage Tracking', desc: 'Easily tracks workers\' mileage with precision and accuracy.'
  },
  {
    icon: <MdAccessTime />, title: 'Work Hours', desc: 'Easily tracks workers\' hours for accurate payroll and reporting.'
  },
  {
    icon: <MdDescription />, title: 'Automated Invoicing', desc: 'Automatically generates and sends invoices to clients.'
  },
  {
    icon: <MdSend />, title: 'Invoice Delivery', desc: 'Automatically sends invoices to clients for faster payment.'
  },
  {
    icon: <MdAssessment />, title: 'Reports', desc: 'Generates various reports for business insights and compliance.'
  },
  {
    icon: <MdAttachMoney />, title: 'Payroll Calculation', desc: 'Automatically calculates workers\' pay based on tracked data.'
  },
  {
    icon: <MdIntegrationInstructions />, title: 'QuickBooks Integration', desc: 'Allows for easy integration of invoices into QuickBooks Accounting Software.'
  },
];

const AboutContainer = styled.div`
  .body {
    font-family: 'Inter', sans-serif;
    background: #f8f9fa;
  }

  .section {
    &.is--hero {
      padding-top: 20px;
      padding-bottom: 0;
      text-align: center;
    }
    &.is--content {
      padding: 2.5rem 0 0 0;
      background: none;
    }
    &.is--features {
      background: #f8f9fa;
      padding: 5vw 0 5vw 0;
    }
  }

  .h1 {
    margin: 0;
    font-size: 2.5rem;
    font-weight: bold;
    color: #2c3e50;
    letter-spacing: -1px;
  }

  .content-box {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.08);
    padding: 2rem 2rem 1.5rem 2rem;
    margin: 0 auto 2rem auto;
  }

  .text-muted {
    color: #4a5568;
    margin-bottom: 1.2rem;
    line-height: 1.7;
    font-size: 1.08rem;
  }

  .features-section {
    margin-top: 0;
    h2 {
      text-align: center;
      font-size: 2.2rem;
      color: #1a237e;
      margin-bottom: 2.5rem;
      font-weight: 800;
      letter-spacing: -1px;
      position: relative;
    }
    h2::after {
      content: '';
      display: block;
      margin: 0.5rem auto 0 auto;
      width: 60px;
      height: 4px;
      background: #3498db;
      border-radius: 2px;
    }
    .content-box {
      margin-top: 0;
      box-shadow: 0 4px 24px rgba(0,0,0,0.08);
      padding: 2rem 2rem 1.5rem 2rem;
    }
    ul {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
      gap: 1.5rem;
      padding: 0;
      margin: 0 auto 1.5rem auto;
      max-width: 900px;
    }
    li {
      background: #fff;
      border-radius: 16px;
      box-shadow: 0 4px 24px rgba(52,152,219,0.08);
      padding: 1.5rem 1.5rem 1.5rem 4.2rem;
      position: relative;
      font-size: 1.08rem;
      color: #2d3748;
      transition: box-shadow 0.2s, transform 0.2s, background 0.2s;
      cursor: default;
      margin-bottom: 0;
      border-left: 4px solid #3498db;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      min-height: 120px;
      animation: fadeInUp 0.7s both;
    }
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(30px);}
      to { opacity: 1; transform: translateY(0);}
    }
    .feature-icon {
      position: absolute;
      left: 1.2rem;
      top: 1.2rem;
      background: #e3f1fb;
      color: #3498db;
      border-radius: 50%;
      width: 2.2rem;
      height: 2.2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.4rem;
      box-shadow: 0 2px 8px rgba(52,152,219,0.08);
    }
    .feature-title {
      font-weight: 700;
      font-size: 1.1rem;
      margin-bottom: 0.3rem;
    }
    .feature-desc {
      font-size: 0.98rem;
      color: #4a5568;
    }
    li:hover {
      box-shadow: 0 8px 32px rgba(52,152,219,0.13);
      transform: translateY(-4px) scale(1.03);
      background: #f7fbff;
    }
  }

  @media (max-width: 600px) {
    .content-box, .features-section .content-box { padding: 1rem; }
    .h1 { font-size: 1.6rem; }
    .features-section ul {
      grid-template-columns: 1fr;
    }
    .features-section li {
      padding: 1rem 1rem 1rem 3.2rem;
      min-height: 90px;
    }
    .features-section .feature-icon {
      left: 0.7rem;
      top: 0.7rem;
      width: 1.7rem;
      height: 1.7rem;
      font-size: 1.1rem;
    }
  }
`;

const About = () => {
  return (
    <AboutContainer>
      <div className="body">
        <Header />
        <div className="section is--hero">
          <div className="container-2">
            <h1 className="h1">About Us</h1>
          </div>
        </div> 
        <div className="section is--content">
          <div className="container-2">
            <div className="content-box">
              <p className="text-muted">
                Tracking field workers in the healthcare industry is often challenging! Healthcare companies face the following issues: how to effectively and efficiently track workers' mileage, track workers' hours, calculate workers' pay, invoice clients, and integrate transactions into their financial statements. Research shows that infusion nursing companies, specialty pharmacies and home health companies lose hundreds and thousands of dollars due to incorrect data/information capture. These companies' Human resources departments are also having difficulty organizing pay schedules. The process involves multiple transactions that are prone to human error. Eworxs eliminates human error and increases cost savings!
              </p>
              <p className="text-muted">
                Eworxs automates the entire process. The founder/management has a solid background in accounting with various U.S. and international accounting designations and a strong background in business management, finance, and taxation. The inspiration for building this software was to produce a software that would simplify the business process while maintaining the efficiency of data/information needed for business decision-making!
              </p>
              <p className="text-muted">
                Using Eworxs makes the difference; it simplifies the business process!
              </p>
            </div>
          </div>
        </div>
        <div className="section is--features">
          <div className="container-2">
            <div className="features-section">
              <h2>Key Features</h2>
              <div className="content-box">
                <ul>
                  {features.map((feature, idx) => (
                    <li key={idx}>
                      <span className="feature-icon">{feature.icon}</span>
                      <span className="feature-title">{feature.title}</span>
                      <span className="feature-desc">{feature.desc}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-muted" style={{marginBottom: 0}}>
                  Eworxs is also user-friendly; users must not be tech-savvy to use the app. Your business process should not be complicated; EWORXS makes that possible!
                </p>
              </div>
            </div>
          </div>
        </div>
        <FeaturesFooter />
        <Footer />
      </div>
    </AboutContainer>
  );
};

export default About; 